import React, { useState, useRef, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { createDecartClient, models } from '@decartai/sdk';
import './Card.css';

export default function AvatarLive() {
  const { token, hasApiKey } = useOutletContext();
  const [status, setStatus] = useState('Idle');
  const [portraitImageUrl, setPortraitImageUrl] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const outputVideoRef = useRef(null);
  const clientRef = useRef(null);

  useEffect(() => {
    const handlePortraitSelected = (e) => setPortraitImageUrl(e.detail);
    window.addEventListener('portrait-selected', handlePortraitSelected);
    return () => window.removeEventListener('portrait-selected', handlePortraitSelected);
  }, []);

  const startAvatar = async () => {
    if (!portraitImageUrl) {
      setStatus('Please select a portrait image first');
      return;
    }
    setStatus('Connecting Avatar...');
    try {
      const tokenRes = await fetch('/api/realtime-token', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!tokenRes.ok) throw new Error(await tokenRes.text());
      const { apiKey } = await tokenRes.json();

      const model = models.realtime('live_avatar');
      const imageBlob = await fetch(portraitImageUrl).then(r => r.blob());
      const imageFile = new File([imageBlob], 'portrait.jpg', { type: imageBlob.type || 'image/jpeg' });

      const client = createDecartClient({ apiKey });
      clientRef.current = await client.realtime.connect(null, {
        model,
        initialState: { image: imageFile },
        onRemoteStream: (stream) => outputVideoRef.current.srcObject = stream,
      });
      clientRef.current.on('connectionChange', (s) => setStatus(`Status: ${s}`));
      clientRef.current.on('error', (e) => setStatus(`Error: ${e.message}`));
      const prompt = document.getElementById('avatar-prompt')?.value || "Speak warmly and nod";
      clientRef.current.setPrompt(prompt, { enhance: true });
    } catch (err) {
      setStatus(`Failed: ${err.message}`);
    }
  };

  const stopAvatar = () => {
    if (clientRef.current) clientRef.current.disconnect();
    outputVideoRef.current.srcObject = null;
    setStatus('Disconnected');
  };

  const playAudio = async () => {
    if (!clientRef.current) return;
    if (!audioFile) {
      setStatus('Select an audio file first.');
      return;
    }
    setStatus('Playing audio...');
    await clientRef.current.playAudio(audioFile);
    setStatus('Audio finished.');
  };

  return (
    <div className="card">
      <div className="card-title">
        <i className="fas fa-user-circle"></i> Avatar Live
      </div>
      <label>Behavior Prompt</label>
      <input id="avatar-prompt" defaultValue="Speak warmly and nod" />
      <label>Audio File (optional)</label>
      <input type="file" accept="audio/*" onChange={e => setAudioFile(e.target.files[0])} />
      <div className="reference-preview">
        <strong>Selected Portrait:</strong>
        <div className="preview-box">
          {portraitImageUrl ? (
            <img src={portraitImageUrl} alt="Portrait" />
          ) : (
            <div className="preview-placeholder">None</div>
          )}
        </div>
      </div>
      <div className="card-buttons">
        <button className="btn-primary" onClick={startAvatar} disabled={!hasApiKey || !portraitImageUrl}>Start Avatar</button>
        <button onClick={stopAvatar}>Stop</button>
        <button onClick={playAudio} disabled={!clientRef.current}>Play Audio</button>
      </div>
      <div className="video-box"><p>Avatar Output</p><video ref={outputVideoRef} autoPlay playsInline></video></div>
      <div className="status-badge">{status}</div>
    </div>
  );
}
